/**
 * studio/src/lib/media.ts (2026-06-26).
 *
 * Tiny wrappers around the two Appwrite functions backing the
 * Studio /media page:
 *   - `listMyMedia`  -> GET  /functions/media-asset-library/executions/assets?…
 *   - `deleteMyMedia` -> POST /functions/media-asset-delete/executions/assets/:assetId
 *
 * Both calls go through `invokeFunction` in `appwrite.ts`, which
 * uses the official Appwrite Web SDK's `functions.createExecution`.
 * The previous raw-`fetch()` shape (2026-06-25) was bypassed for two
 * reasons:
 *   - It required a `VITE_APPWRITE_FUNCTION_ENDPOINT` env var that
 *     does NOT exist in `.env` / `.env.example` (the project only
 *     ships `VITE_APPWRITE_ENDPOINT` + `VITE_APPWRITE_PROJECT_ID`),
 *     so the dev page rendered an immediate "is not set" error.
 *   - The manual `fetch()` sent custom headers (X-Appwrite-Project),
 *     which Appwrite 1.9.5 self-hosted's CORS allow-list does NOT
 *     whitelist at the preflight. The SDK path uses `x-sdk-*`
 *     headers only and rides the session cookie via
 *     `credentials: "include"`. See `appwrite.ts` `invokeFunction`
 *     JSDoc for the full CORS rationale.
 *
 * Auth: both calls rely on the Appwrite session cookie set by the
 * Studio auth flow. The Appwrite runtime injects
 * `x-appwrite-user-id` from the session, which is the trust
 * boundary the Appwrite functions read.
 */
import { ExecutionMethod } from "appwrite";
import { isHiddenSystemAssetKind } from "@honest-pitches/pitch-asset-core";
import {
  invokeFunction,
  MEDIA_ASSET_LIBRARY_FUNCTION_ID,
  MEDIA_ASSET_DELETE_FUNCTION_ID,
  MY_VIDEOS_LIST_FUNCTION_ID,
  MY_VIDEOS_DELETE_FUNCTION_ID,
} from "./appwrite.js";

export type MediaAssetKind =
  | "thumbnail"
  | "video_poster"
  | "og_image"
  | "segment_image"
  | "attachment"
  | "bunny_video";

export const MEDIA_ASSET_KINDS: readonly MediaAssetKind[] = [
  "thumbnail",
  "video_poster",
  "og_image",
  "segment_image",
  "attachment",
  "bunny_video",
];

/** Image-library kinds only — `bunny_video` rows belong on the Videos tab. */
export const IMAGE_MEDIA_ASSET_KINDS: readonly MediaAssetKind[] =
  MEDIA_ASSET_KINDS.filter((k) => k !== "bunny_video");

export interface MediaAsset {
  $id: string;
  kind: MediaAssetKind;
  cdnUrl: string;
  bunnyPath: string;
  mimeType: string;
  sizeBytes: number;
  widthPx: number | null;
  heightPx: number | null;
  uploadedAt: string;
  pitchId: string;
  pitchTitle: string;
  pitchStatus: string;
  pitchVisibility: string;
  inUseOnPublishedPitch: boolean;
}

export interface ListMyMediaResponse {
  ok: true;
  assets: MediaAsset[];
  total: number;
  limit: number;
  offset: number;
}

export interface DeleteMyMediaResponse {
  ok: true;
  deletedAssetId: string;
  bunnyPathDeleted: string;
}

export interface MediaAssetError {
  ok: false;
  status: number;
  error: string;
  blockingPitchIds?: string[];
}

export function isMediaAssetKind(v: unknown): v is MediaAssetKind {
  return (
    typeof v === "string" &&
    (MEDIA_ASSET_KINDS as readonly string[]).includes(v)
  );
}

/** System-generated rows (e.g. share_qr) must not appear in /media or pickers. */
export function isCreatorLibraryAsset(row: { kind?: string | null }): boolean {
  return !isHiddenSystemAssetKind(String(row.kind ?? ""));
}

export function filterCreatorLibraryAssets<T extends { kind?: string }>(
  rows: readonly T[],
): T[] {
  return rows.filter((row) => isCreatorLibraryAsset(row));
}

/** Rows for the Images / All tabs' image half — no videos, no system assets. */
export function filterImageLibraryAssets<T extends { kind?: string }>(
  rows: readonly T[],
): T[] {
  return filterCreatorLibraryAssets(rows).filter((row) => row.kind !== "bunny_video");
}

/** OG / pitch-image card aspect (1200×630). Shared by /media + picker grids. */
export const PITCH_IMAGE_ASPECT_RATIO = "1200 / 630";

/**
 * Historical `pitch_assets.cdnUrl` rows omit `https://`. Browsers treat
 * scheme-less values as relative to the Studio origin and 404.
 */
export function ensureCdnUrl(url: string): string {
  if (!url) return url;
  if (url.startsWith("//")) return `https:${url}`;
  if (!/^https?:\/\//i.test(url)) return `https://${url}`;
  return url;
}

/**
 * Bunny Stream VoD pull-zone hostname (thumbnail.jpg, play_*.mp4).
 *
 * Resolution: `import.meta.env.VITE_BUNNY_STREAM_CDN_BASE_URL` — mirrored
 * in `honest-pitches-studio/.env` (dev) and `.env.production` (deploy).
 * Same value as `BUNNY_STREAM_CDN_BASE_URL` in `honest-pitches-appwrite/.env`.
 *
 * Per https://docs.bunny.net/stream/storage-structure — auto-generated
 * posters live at `https://{pull_zone}/{video_id}/thumbnail.jpg`.
 */
export function bunnyStreamPullZoneBaseUrl(): string | null {
  const raw = String(
    (typeof import.meta !== "undefined" && import.meta.env?.VITE_BUNNY_STREAM_CDN_BASE_URL) ||
      "",
  ).trim();
  if (!raw) return null;
  return ensureCdnUrl(raw).replace(/\/+$/, "");
}

function uniqueThumbnailUrls(urls: Array<string | null | undefined>): string[] {
  const seen = new Set<string>();
  const out: string[] = [];
  for (const url of urls) {
    if (!url || seen.has(url)) continue;
    seen.add(url);
    out.push(url);
  }
  return out;
}

function streamPosterOnPullZone(pullZoneOrigin: string, bunnyVideoId: string, fileName: string): string {
  const base = pullZoneOrigin.replace(/\/+$/, "");
  return `${base}/${bunnyVideoId}/${fileName}`;
}

function pullZoneOriginFromPlaybackUrl(cdn: string): string | null {
  try {
    const u = new URL(cdn);
    if (/\.b-cdn\.net$/i.test(u.hostname)) return u.origin;
  } catch {
    /* malformed URL */
  }
  return null;
}

function thumbnailUrlsFromPlaybackCdn(cdn: string, bunnyVideoId: string): string[] {
  const swapped = cdn
    .replace(/\/play_\d+p\.mp4$/i, "/thumbnail.jpg")
    .replace(/\/playlist\.m3u8$/i, "/thumbnail.jpg");
  if (swapped === cdn) return [];

  if (!bunnyVideoId) return [swapped];

  try {
    const u = new URL(swapped);
    const segments = u.pathname.split("/").filter(Boolean);
    const thumbIdx = segments.lastIndexOf("thumbnail.jpg");
    if (thumbIdx < 1) return [swapped];

    const parentId = segments[thumbIdx - 1];
    if (parentId && parentId !== bunnyVideoId) {
      const prefix = segments.slice(0, thumbIdx - 1);
      const rebuilt = `${u.origin}/${[...prefix, bunnyVideoId, "thumbnail.jpg"].join("/")}`;
      return uniqueThumbnailUrls([
        rebuilt,
        streamPosterOnPullZone(u.origin, bunnyVideoId, "thumbnail.jpg"),
        swapped,
      ]);
    }
  } catch {
    return [swapped];
  }

  return [swapped];
}

/**
 * Ordered Bunny Stream poster candidates for a `pitch_videos` row.
 *
 * `pitch_videos.cdnUrl` is often empty (presign does not set it; the
 * webhook writes HLS to `pitch_assets` only). When `cdnUrl` is missing
 * we build from `bunnyVideoId` + `VITE_BUNNY_STREAM_CDN_BASE_URL`.
 *
 * When `cdnUrl` is `video.bunnycdn.com/.../playlist.m3u8`, thumbnails
 * still live on the Stream pull zone — not on `video.bunnycdn.com`.
 *
 * Per Bunny docs, fall back through `preview.webp` and numbered
 * `thumbnail_N.jpg` frames when the default poster is not ready yet.
 *
 * The library grid must NOT use the HLS/MP4 URL in `<video src>`;
 * that path often renders as a black box while metadata loads.
 */
export function bunnyVideoThumbnailCandidates(video: {
  cdnUrl?: string | null;
  bunnyVideoId?: string | null;
}): string[] {
  const bunnyId = (video.bunnyVideoId ?? "").trim();
  const cdn = ensureCdnUrl((video.cdnUrl ?? "").trim());
  const envPullZone = bunnyStreamPullZoneBaseUrl();
  const candidates: string[] = [];

  if (cdn) {
    if (/video\.bunnycdn\.com/i.test(cdn) && bunnyId) {
      const origin = envPullZone ?? pullZoneOriginFromPlaybackUrl(cdn);
      if (origin) candidates.push(streamPosterOnPullZone(origin, bunnyId, "thumbnail.jpg"));
    } else {
      candidates.push(...thumbnailUrlsFromPlaybackCdn(cdn, bunnyId));
      const origin = pullZoneOriginFromPlaybackUrl(cdn);
      if (bunnyId && origin) {
        candidates.push(streamPosterOnPullZone(origin, bunnyId, "thumbnail.jpg"));
      }
    }

    const embedMatch = cdn.match(/mediadelivery\.net\/embed\/[^/]+\/([^/?#]+)/i);
    if (embedMatch?.[1] && envPullZone) {
      candidates.push(streamPosterOnPullZone(envPullZone, embedMatch[1], "thumbnail.jpg"));
    }

    if (bunnyId) {
      try {
        const u = new URL(cdn);
        const segments = u.pathname.split("/").filter(Boolean);
        if (segments.includes(bunnyId)) {
          const idx = segments.indexOf(bunnyId);
          candidates.push(
            `${u.origin}/${[...segments.slice(0, idx + 1), "thumbnail.jpg"].join("/")}`,
          );
        }
      } catch {
        /* ignore */
      }
    }
  }

  if (bunnyId && envPullZone) {
    candidates.push(streamPosterOnPullZone(envPullZone, bunnyId, "thumbnail.jpg"));
  }

  const primaries = uniqueThumbnailUrls(candidates);
  if (primaries.length === 0) return [];

  const first = primaries[0]!;
  const fallbacks = [
    first.replace(/thumbnail\.jpg$/i, "preview.webp"),
    ...[1, 2, 3, 4, 5].map((n) => first.replace(/thumbnail\.jpg$/i, `thumbnail_${n}.jpg`)),
  ];
  return uniqueThumbnailUrls([...primaries, ...fallbacks]);
}

/** First Bunny Stream poster candidate, or `null` when none can be built. */
export function bunnyVideoThumbnailUrl(video: {
  cdnUrl?: string | null;
  bunnyVideoId?: string | null;
}): string | null {
  return bunnyVideoThumbnailCandidates(video)[0] ?? null;
}

export function videoLibraryStatusLabel(status: string): string {
  switch (status) {
    case "ready":
      return "Ready";
    case "created":
      return "Queued";
    case "uploading":
      return "Uploading";
    case "processing":
      return "Processing";
    case "failed":
      return "Failed";
    default:
      return status;
  }
}

/** One-line hint on non-ready cards in the library grid. */
export function videoPickBlockedShortHint(status: string): string | null {
  if (status === "ready") return null;
  switch (status) {
    case "created":
      return "Queued — not selectable yet";
    case "uploading":
      return "Upload in progress";
    case "processing":
      return "Encoding — pick when Ready";
    case "failed":
      return "Upload failed — delete and retry";
    default:
      return "Not ready to attach";
  }
}

/** Full explanation when the user clicks a non-ready row. */
export function videoPickBlockedReason(status: string): string | null {
  if (status === "ready") return null;
  switch (status) {
    case "created":
      return "This clip is still queued. Finish uploading on the Upload new tab, or wait a moment for the upload to start. Only Ready clips can be attached to a pitch.";
    case "uploading":
      return "The upload is still running. Wait until it finishes and the status changes to Ready (usually a few minutes after encoding completes).";
    case "processing":
      return "This clip is still encoding. You can attach it once the status shows Ready — refresh the library if it has been more than a few minutes.";
    case "failed":
      return "This upload failed on the server. Delete it from My Media and upload again.";
    default:
      return `This clip is not ready yet (status: ${status}). Only Ready clips can be attached to a pitch.`;
  }
}

/**
 * GET the calling pitcher's pitch_assets library page.
 * Returns the parsed `ListMyMediaResponse`. Throws on network /
 * parsing errors; surfaces 4xx/5xx as `Error` with the server's
 * message.
 *
 * Query params are encoded in the function `path` because the
 * Appwrite SDK's GET execution reads `path` (not the body) for
 * query params — confirmed via the official
 * `appwrite.io/docs/references/cloud/client-web/functions` docs
 * (2026-06-26).
 */
export async function listMyMedia(args: {
  kind?: MediaAssetKind | null;
  limit?: number;
  offset?: number;
} = {}): Promise<ListMyMediaResponse> {
  const params = new URLSearchParams();
  if (args.kind) params.set("kind", args.kind);
  params.set("limit", String(args.limit ?? 24));
  params.set("offset", String(args.offset ?? 0));
  const path = `/assets?${params.toString()}`;
  return (await invokeFunction(
    MEDIA_ASSET_LIBRARY_FUNCTION_ID,
    {},
    path,
    ExecutionMethod.GET,
  )) as unknown as ListMyMediaResponse;
}

/**
 * Delete one asset. Returns `DeleteMyMediaResponse` on success.
 * Throws an Error whose `.message` is the server's `error` field
 * (typically `"asset is in use on a published pitch; remove it
 * from the pitch first"` for the 403 in-use case). The caller can
 * show the message verbatim — no structured `.status` /
 * `.blockingPitchIds` fields are attached today because the
 * Studio /media view only renders the message string.
 *
 * The function accepts both POST and DELETE on the same path
 * (see `media-asset-delete/src/dispatch.ts`); we use POST because
 * the Appwrite browser SDK's `createExecution` defaults to POST
 * and we don't need to set a body.
 */
export async function deleteMyMedia(args: {
  assetId: string;
}): Promise<DeleteMyMediaResponse> {
  if (!args.assetId) {
    throw new Error("assetId is required");
  }
  const path = `/assets/${encodeURIComponent(args.assetId)}`;
  return (await invokeFunction(
    MEDIA_ASSET_DELETE_FUNCTION_ID,
    {},
    path,
    ExecutionMethod.POST,
  )) as unknown as DeleteMyMediaResponse;
}

// ─── Pitch videos (Bunny Stream, My Videos page) ─────────────────────────────

/**
 * Single-row shape returned by `my-videos-list` and the source of
 * truth for `MyVideosView.vue`. Mirrors the function's
 * `VideoListItem` interface.
 */
export interface MyVideo {
  $id: string;
  pitchId: string;
  bunnyVideoId: string;
  cdnUrl: string;
  status: "uploading" | "ready" | "failed" | "processing" | "created";
  durationSeconds: number | null;
  title: string | null;
  purpose: "hero" | "section" | null;
  sectionId: string | null;
  uploadedByUserId: string;
  createdAt: string;
  updatedAt: string;
  /**
   * True when referenced by a live+public pitch (hero or section).
   * Returned by `my-videos-list`; gates the delete button in
   * MyMediaView's Videos / All tabs.
   */
  inUseOnPublishedPitch?: boolean;
}

export interface ListMyVideosResponse {
  ok: true;
  videos: MyVideo[];
  total: number;
  limit: number;
  offset: number;
}

export interface DeleteMyVideoResponse {
  ok: true;
  deletedVideoId: string;
  bunnyVideoIdDeleted: string;
}

export interface DeleteMyVideoError {
  ok: false;
  status: number;
  error: string;
  blockingPitchIds?: string[];
}

/**
 * Delete one `pitch_videos` row owned by the calling pitcher
 * (2026-06-30). Server-side guard refuses with 403 +
 * `blockingPitchIds` if any live+public pitch still references the
 * video (hero via `pitches.primaryVideoId`, or section via
 * `pitch_sections.bunnyVideoId`). Returns the parsed
 * `DeleteMyVideoResponse` on success.
 *
 * Throws an `Error` whose `.message` is the server's `error` field
 * (typically `"video is in use on a published pitch; remove it from
 * the pitch first"` for the 403 in-use case). The MyVideosView
 * renders the message verbatim.
 *
 * The function accepts both POST and DELETE on the same path (see
 * `my-videos-delete/src/dispatch.ts`); we use POST because the
 * Appwrite browser SDK's `createExecution` defaults to POST and we
 * don't need to set a body. The video id comes from the URL path,
 * never the body, so neither verb needs a payload.
 */
export async function deleteMyVideo(args: {
  videoId: string;
}): Promise<DeleteMyVideoResponse> {
  if (!args.videoId) {
    throw new Error("videoId is required");
  }
  const path = `/videos/${encodeURIComponent(args.videoId)}`;
  return (await invokeFunction(
    MY_VIDEOS_DELETE_FUNCTION_ID,
    {},
    path,
    ExecutionMethod.POST,
  )) as unknown as DeleteMyVideoResponse;
}

/**
 * 2026-07-04 (unify-my-media-with-videos-tab plan): list
 * `pitch_videos` for the calling pitcher. Shared helper used by both
 * MyVideosView.vue (now a redirect wrapper) and MyMediaView.vue's
 * Videos / All tabs. Mirrors `listMyMedia`'s shape.
 *
 * The status filter is optional; when `null` (or "all"), the server
 * returns every row the caller owns.
 */
export type MyVideoStatusFilter =
  | "ready"
  | "uploading"
  | "processing"
  | "created"
  | "failed";

export async function listMyVideos(args: {
  status?: MyVideoStatusFilter | "all" | null;
  limit?: number;
  offset?: number;
} = {}): Promise<ListMyVideosResponse> {
  const params = new URLSearchParams();
  if (args.status && args.status !== "all") {
    params.set("status", args.status);
  }
  params.set("limit", String(args.limit ?? 24));
  params.set("offset", String(args.offset ?? 0));
  const path = `/videos?${params.toString()}`;
  return (await invokeFunction(
    MY_VIDEOS_LIST_FUNCTION_ID,
    {},
    path,
    ExecutionMethod.GET,
  )) as unknown as ListMyVideosResponse;
}